/* eslint-disable */
// entityRoutes — ONE definition of "can this notification be opened, and where".
//
// Four call sites use it: the corner card, the important bar, the critical modal
// and the drawer row. One rule, four consumers — two definitions of this would
// drift, and then one surface offers an Open that another does not.
//
// ⚠️ NEVER RENDER A BUTTON THAT CANNOT WORK. A dead `Open` is worse than no
// `Open`: it teaches people the system is broken and they stop trusting the ones
// that do work. canOpen() is the guard, and every call site checks it first.
//
// Convention #136 — this app is state-based, not routed. No react-router, no
// history.push. Navigation is a uiStore setTab plus, where one exists, the
// portal's own open-bridge. That is exactly what global search already does
// (GlobalSearchMount's handleGlobalSelect), and this reuses its mechanism rather
// than inventing a second way to reach a lens.

import { useUiStore } from '../stores/uiStore';
import { requestOpenLens } from '../portals/data/lensBank/lensOpenBridge';
import { requestOpenLtd } from '../portals/data/lensBank/lensDev/lensDevOpenBridge';
import { requestOpenOrdersLine, requestOpenDeskOrder, requestOpenCollectionWall } from '../portals/orders/shell/ordersOpenBridge';

// KNOWN entity types. A value of null means "we know this type and it has no
// destination yet" — no button, and deliberately NO warning, because warning on
// every orders notification would be noise about a gap we already know about.
// A type absent from this map entirely is genuinely unexpected and does warn.
const ROUTES = {
  none: null,          // system.announcement, sales.sync_completed, system.outage
  lens: (id) => {      // lens.favorited — the only type with a live destination
    const ui = useUiStore.getState();
    ui.setEntryPhase('app');
    ui.setTab('lensBank');
    requestOpenLens(id);
  },
  qc_report: null,     // qc.report_approved — no open-a-report bridge exists yet
  // orders.import_failed (entity 'order') and orders.comment_mention (entity
  // 'orders_line') both carry an orders_lines id. B1a: the Book's Table opens
  // on that line through the bridge, exactly as lens does through its own.
  order: (id) => {
    const ui = useUiStore.getState();
    ui.setEntryPhase('app');
    requestOpenOrdersLine(id);
    ui.setTab('orders');
  },
  orders_line: (id) => {
    const ui = useUiStore.getState();
    ui.setEntryPhase('app');
    requestOpenOrdersLine(id);
    ui.setTab('orders');
  },
  // B5-1 c3: the clock's seven types (orders.sheet_waiting … orders.draft_stale)
  // carry entity_type 'pi' with entity_id = orders_pis.id — the Desk's order,
  // not a Book line. The bridge's Desk slot opens the order card, exactly as
  // the line slot opens the Table on a line (Gate D: the mechanism existed; a
  // second slot, no ?pi= address).
  pi: (id) => {
    const ui = useUiStore.getState();
    ui.setEntryPhase('app');
    requestOpenDeskOrder(id);
    ui.setTab('orders');
  },
  // C7 · orders.collection_moved carries entity_type 'collection_run' with
  // entity_id = the sync run's report; the card opens the Collection Wall.
  collection_run: (id) => {
    const ui = useUiStore.getState();
    ui.setEntryPhase('app');
    requestOpenCollectionWall(id);
    ui.setTab('orders');
  },
};

const warned = new Set();

// True only when there is somewhere to go AND something to identify.
export function canOpen(entityType, entityId) {
  if (!entityType || entityType === 'none') return false;
  if (!entityId) return false;
  if (!(entityType in ROUTES)) {
    if (!warned.has(entityType)) {
      warned.add(entityType);   // once per type per session, not once per render
      console.warn(`[notifications] no route for entity_type "${entityType}" — Open hidden`);
    }
    return false;
  }
  return typeof ROUTES[entityType] === 'function';
}

// Navigate. Returns true when it actually went somewhere, so callers can decide
// whether to stamp read_at on the strength of it.
export function openEntity(entityType, entityId) {
  if (!canOpen(entityType, entityId)) return false;
  ROUTES[entityType](entityId);
  return true;
}

// ---------------------------------------------------------------------------
// P6 — attachments. Same file on purpose: "where does this go" has ONE
// definition, and an attachment is just another thing with a destination.
//
// These take the RESOLVED entity, not a {kind,id} ref, because two of the four
// bridges are keyed by CODE rather than id (requestOpenLens takes lens_no,
// requestOpenLtd takes ltd_code) — and the code is only known after the reader
// has fetched the row under their own RLS. Which is the same reason a reader
// without access gets no button: there is no code to navigate with.
//
// ⚠️ P6 SAID `item` HAD NO BRIDGE. THAT WAS WRONG, AND THIS IS THE CORRECTION.
// P6 looked at GlobalSearchMount, found it opening an item through LOCAL
// setSelected state, and concluded no reusable path existed. It never grepped
// for a catalog-item event. One has existed all along: `open_catalog_item`,
// listened for in App.js:363-372, which resolves the row from memory OR FETCHES
// IT BY ID when it is not loaded, switches to the catalog tab and opens the
// detail modal. Three surfaces already dispatch it — DevCompareModal.js:120,
// useDevNumberLink.js:92 and DevRequestsPortal.js:66 — so it is the established
// pattern, not a new one. `item` was the one family David could not open, and
// the reason was a gap in my search, not in the codebase.
const ATTACH_ROUTES = {
  item: (e) => {
    const ui = useUiStore.getState();
    ui.setEntryPhase('app');
    // App.js's listener does the tab switch and the fetch-if-absent itself; the
    // 100ms mirrors the other event bridges, whose listeners mount with the tab.
    setTimeout(() => {
      try { window.dispatchEvent(new CustomEvent('open_catalog_item', { detail: { id: e.id } })); } catch (_) {}
    }, 100);
  },
  dev: (e) => {
    const ui = useUiStore.getState();
    ui.setEntryPhase('app');
    ui.setTab('developments');
    // 100ms mirrors GlobalSearchMount: the panel listens only once mounted.
    setTimeout(() => {
      try { window.dispatchEvent(new CustomEvent('open_dev_panel', { detail: { id: e.id } })); } catch (_) {}
    }, 100);
  },
  lens: (e) => {
    const ui = useUiStore.getState();
    ui.setEntryPhase('app');
    ui.setTab('lensBank');
    requestOpenLens(e.code);
  },
  lensdev: (e) => {
    const ui = useUiStore.getState();
    ui.setEntryPhase('app');
    ui.setTab('lensBank');
    requestOpenLtd(e.code);
  },
};

export function canOpenAttachment(entity) {
  if (!entity || !entity.kind) return false;
  if (!(entity.kind in ATTACH_ROUTES)) return false;
  if (typeof ATTACH_ROUTES[entity.kind] !== 'function') return false;
  // lens and lensdev navigate by CODE; item and dev by id. No code, nowhere to go.
  if ((entity.kind === 'lens' || entity.kind === 'lensdev') && !entity.code) return false;
  return !!entity.id;
}

export function openAttachment(entity) {
  if (!canOpenAttachment(entity)) return false;
  ATTACH_ROUTES[entity.kind](entity);
  return true;
}

// Convenience for the four call sites, which all hold a `notifications` row.
export function canOpenNotification(n) {
  return canOpen(n?.entity_type, n?.entity_id);
}
export function openNotification(n) {
  return openEntity(n?.entity_type, n?.entity_id);
}
